/**
 * SINGLE SOURCE OF TRUTH for all marketplace liability / disclaimer copy.
 *
 * Every place that shows the disclaimer (registration, lead checkout,
 * subscription checkout, Terms of Service, Privacy Policy, footer) imports
 * from this file so the legal language can never drift between screens.
 */

export type Lang2 = 'en' | 'es';

/** Bump when the legal copy materially changes — stored on each acceptance. */
export const TERMS_VERSION = '2026-09-09';

type Bi = { en: string; es: string };
type BiList = { en: string[]; es: string[] };

/** Short one-line notice used under buttons and inside compact panels. */
export const SHORT_DISCLAIMER: Bi = {
  en: 'This platform is a technology marketplace that connects homeowners, general contractors and subcontractors. We do not perform contracting services, supervise projects, or guarantee the quality of any work. You are responsible for your own due diligence.',
  es: 'Esta plataforma es un mercado tecnológico que conecta a dueños de casa, contratistas generales y subcontratistas. No realizamos servicios de contratación, no supervisamos proyectos ni garantizamos la calidad de ningún trabajo. Usted es responsable de su propia diligencia debida.',
};

/** Full marketplace disclaimer body paragraphs. */
export const FULL_DISCLAIMER: BiList = {
  en: [
    'The platform acts only as a technology marketplace that facilitates introductions and opportunity discovery between homeowners, general contractors and subcontractors.',
    'The platform does NOT perform contracting services. The platform does NOT supervise projects. The platform does NOT guarantee the quality of any work performed.',
    'The platform does NOT verify all licenses, certifications, insurance policies, qualifications, references, project history, or legal eligibility of users unless explicitly stated on a profile with a verification badge.',
    'Users are solely responsible for performing their own due diligence before entering into any agreement, hiring decision, project contract, work order, subcontracting agreement, or payment arrangement.',
    'The platform is not responsible for work quality, construction defects, injuries, damages, delays, contract disputes, payment disputes, permitting issues, licensing issues, insurance claims, or regulatory compliance failures.',
    'The platform does not guarantee that any project, work order, lead, contractor, subcontractor, homeowner, opportunity, budget, timeline, or payment information is accurate, complete, current, or verified. Users assume all risk when engaging with other parties on the platform.',
  ],
  es: [
    'La plataforma actúa únicamente como un mercado tecnológico que facilita presentaciones y el descubrimiento de oportunidades entre dueños de casa, contratistas generales y subcontratistas.',
    'La plataforma NO realiza servicios de contratación. La plataforma NO supervisa proyectos. La plataforma NO garantiza la calidad de ningún trabajo realizado.',
    'La plataforma NO verifica todas las licencias, certificaciones, pólizas de seguro, calificaciones, referencias, historial de proyectos ni la elegibilidad legal de los usuarios, salvo que se indique expresamente en un perfil con una insignia de verificación.',
    'Los usuarios son los únicos responsables de realizar su propia diligencia debida antes de celebrar cualquier acuerdo, decisión de contratación, contrato de proyecto, orden de trabajo, acuerdo de subcontratación o arreglo de pago.',
    'La plataforma no es responsable por la calidad del trabajo, defectos de construcción, lesiones, daños, retrasos, disputas contractuales, disputas de pago, problemas de permisos, problemas de licencias, reclamos de seguros ni incumplimientos regulatorios.',
    'La plataforma no garantiza que ningún proyecto, orden de trabajo, lead, contratista, subcontratista, dueño de casa, oportunidad, presupuesto, cronograma o información de pago sea exacto, completo, actual o verificado. Los usuarios asumen todo el riesgo al interactuar con otras partes en la plataforma.',
  ],
};

/** Role specific verification responsibilities. */
export const ROLE_RESPONSIBILITIES: Record<'homeowner' | 'gc' | 'subcontractor', { title: Bi; items: BiList }> = {
  homeowner: {
    title: { en: 'As a homeowner you are responsible for verifying:', es: 'Como dueño de casa usted es responsable de verificar:' },
    items: {
      en: ['Licenses', 'Insurance', 'References', 'Qualifications', 'Contractor suitability for your project'],
      es: ['Licencias', 'Seguros', 'Referencias', 'Calificaciones', 'Idoneidad del contratista para su proyecto'],
    },
  },
  gc: {
    title: { en: 'As a general contractor you are responsible for verifying:', es: 'Como contratista general usted es responsable de verificar:' },
    items: {
      en: ['Licenses', 'Insurance', 'Trade qualifications', 'Compliance requirements', 'Workforce requirements'],
      es: ['Licencias', 'Seguros', 'Calificaciones del oficio', 'Requisitos de cumplimiento', 'Requisitos de personal'],
    },
  },
  subcontractor: {
    title: { en: 'As a subcontractor you are responsible for verifying:', es: 'Como subcontratista usted es responsable de verificar:' },
    items: {
      en: ['Project legitimacy', 'Contractor information', 'Payment terms', 'Contract requirements', 'Job conditions'],
      es: ['Legitimidad del proyecto', 'Información del contratista', 'Términos de pago', 'Requisitos del contrato', 'Condiciones del trabajo'],
    },
  },
};

/** Acceptance checkbox labels. */
export const ACCEPT_LABEL: Bi = {
  en: 'I have read and accept the Terms of Service and Privacy Policy, and I understand this platform is a marketplace that does not verify, supervise or guarantee any user, project or work.',
  es: 'He leído y acepto los Términos de Servicio y la Política de Privacidad, y entiendo que esta plataforma es un mercado que no verifica, supervisa ni garantiza a ningún usuario, proyecto o trabajo.',
};

export const ACCEPT_REQUIRED: Bi = {
  en: 'You must accept the Terms of Service to continue.',
  es: 'Debe aceptar los Términos de Servicio para continuar.',
};

export const CHECKOUT_ACK_LABEL: Bi = {
  en: 'I understand this purchase is for marketplace access to an opportunity, that all sales are final and non refundable, and that the platform does not verify, guarantee or supervise the project, the parties, or any outcome.',
  es: 'Entiendo que esta compra es para acceso de mercado a una oportunidad, que todas las ventas son finales y no reembolsables, y que la plataforma no verifica, garantiza ni supervisa el proyecto, las partes ni ningún resultado.',
};

/**
 * BILLING POLICY — single source of truth.
 * No refunds are ever issued automatically by the platform. A lead that fails
 * our quality standards may be REPLACED with another lead of comparable value,
 * at our sole discretion, after a support review.
 */
export const BILLING_POLICY_HEADING: Bi = {
  en: 'Billing policy — all sales final',
  es: 'Política de facturación — todas las ventas son finales',
};

export const BILLING_POLICY: BiList = {
  en: [
    'All sales are final. We do not issue refunds for memberships, subscriptions, lead purchases, used leads or partially used plans.',
    'You may cancel future billing at any time, but payments already collected are non refundable and your plan stays active until the end of the paid period.',
    'If you believe a lead is invalid you must contact support for review. Our team investigates the lead against our lead quality standards.',
    'If we determine a lead is invalid, fraudulent, inaccurate, fake, unreachable, duplicated or otherwise unacceptable, we may replace it with another lead of comparable value. Lead replacement is at our sole discretion and is the only remedy offered.',
    'The platform never issues an automatic refund. No refund is ever triggered by a button, a cancellation or a support ticket on its own.',
  ],
  es: [
    'Todas las ventas son finales. No emitimos reembolsos por membresías, suscripciones, compras de leads, leads usados ni planes parcialmente usados.',
    'Puede cancelar la facturación futura en cualquier momento, pero los pagos ya cobrados no son reembolsables y su plan permanece activo hasta el final del período pagado.',
    'Si considera que un lead no es válido, debe contactar a soporte para su revisión. Nuestro equipo investiga el lead según nuestros estándares de calidad.',
    'Si determinamos que un lead es inválido, fraudulento, inexacto, falso, inalcanzable, duplicado o inaceptable, podemos reemplazarlo por otro lead de valor comparable. El reemplazo de leads es a nuestra entera discreción y es el único remedio ofrecido.',
    'La plataforma nunca emite un reembolso automático. Ningún reembolso se activa por un botón, una cancelación ni un ticket de soporte por sí solos.',
  ],
};

/** Compact version shown inside checkout panels. */
export const CHECKOUT_BILLING_NOTE: Bi = {
  en: 'All sales are final. No refunds on memberships, subscriptions, lead purchases, used leads or partially used plans. Invalid leads may be replaced after a support review, at our sole discretion.',
  es: 'Todas las ventas son finales. Sin reembolsos en membresías, suscripciones, compras de leads, leads usados o planes parcialmente usados. Los leads inválidos pueden ser reemplazados tras una revisión de soporte, a nuestra entera discreción.',
};

/** Support policy shown on the contact page. */
export const SUPPORT_POLICY: BiList = {
  en: [
    'Lead quality reviews: contact support within 7 days of unlocking a lead and include the project, the date and what happened. We investigate every report.',
    'Outcome: if the lead fails our quality standards we replace it with another lead of comparable value. We do not issue refunds.',
    'Billing questions: we can cancel future billing on request. Payments already collected are not returned.',
    'Response time: support replies within one business day in English or Spanish.',
  ],
  es: [
    'Revisiones de calidad de leads: contacte a soporte dentro de los 7 días posteriores a desbloquear un lead e incluya el proyecto, la fecha y lo sucedido. Investigamos cada reporte.',
    'Resultado: si el lead no cumple nuestros estándares de calidad lo reemplazamos por otro lead de valor comparable. No emitimos reembolsos.',
    'Preguntas de facturación: podemos cancelar la facturación futura a solicitud. Los pagos ya cobrados no se devuelven.',
    'Tiempo de respuesta: soporte responde en un día hábil en inglés o español.',
  ],
};

export const DISCLAIMER_HEADING: Bi = {
  en: 'Marketplace disclaimer',
  es: 'Aviso legal del mercado',
};


/** Copy for the badge row — badges only render when truly verified. */
export const BADGE_COPY = {
  license: { en: 'License Verified', es: 'Licencia Verificada' },
  insurance: { en: 'Insurance Verified', es: 'Seguro Verificado' },
  background: { en: 'Background Verified', es: 'Antecedentes Verificados' },
  none: {
    en: 'Not verified by the platform. Verify licensing, insurance and references yourself.',
    es: 'No verificado por la plataforma. Verifique licencias, seguros y referencias usted mismo.',
  },
};

/** Profile settings copy (optional license / insurance collected AFTER signup). */
export const PROFILE_COPY = {
  title: { en: 'Profile Settings', es: 'Configuración de Perfil' },
  subtitle: {
    en: 'Keep your business details current. Licensing and insurance are optional and are never required to register or to use the marketplace.',
    es: 'Mantenga sus datos comerciales actualizados. La licencia y el seguro son opcionales y nunca son requeridos para registrarse o usar el mercado.',
  },
  business: { en: 'Business details', es: 'Datos del negocio' },
  credentials: { en: 'Licensing & insurance (optional)', es: 'Licencia y seguro (opcional)' },
  optionalNote: {
    en: 'Optional. Many subcontractors work under a licensed contractor, are new businesses, or are in the process of obtaining a license. Leaving these blank does not limit your account in any way.',
    es: 'Opcional. Muchos subcontratistas trabajan bajo un contratista con licencia, son negocios nuevos o están en proceso de obtener una licencia. Dejar esto en blanco no limita su cuenta de ninguna manera.',
  },
  licenseNumber: { en: 'License Number', es: 'Número de Licencia' },
  licenseState: { en: 'License State', es: 'Estado de la Licencia' },
  insuranceCarrier: { en: 'Insurance Carrier', es: 'Aseguradora' },
  insurancePolicy: { en: 'Policy Number', es: 'Número de Póliza' },
  verification: { en: 'Verification status', es: 'Estado de verificación' },
  verificationNote: {
    en: 'Badges are granted only after our team completes an actual verification. Submitting information here does not create a badge.',
    es: 'Las insignias se otorgan solo después de que nuestro equipo complete una verificación real. Enviar información aquí no crea una insignia.',
  },
  save: { en: 'Save changes', es: 'Guardar cambios' },
  saving: { en: 'Saving...', es: 'Guardando...' },
  saved: { en: 'Profile updated.', es: 'Perfil actualizado.' },
  acceptedOn: { en: 'Terms accepted on', es: 'Términos aceptados el' },
};

export const bi = (v: Bi, lang: string): string => (lang === 'es' ? v.es : v.en);
export const biList = (v: BiList, lang: string): string[] => (lang === 'es' ? v.es : v.en);
